import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DocumentTextIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  CalendarIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ArrowDownTrayIcon,
  EyeIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

const AttendanceHistory = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [attendanceHistory, setAttendanceHistory] = useState([]);
  const [filteredHistory, setFilteredHistory] = useState([]);
  const [filters, setFilters] = useState({
    dateRange: 'last_month',
    status: 'all',
    workType: 'all',
    search: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    // Generate sample attendance history data
    const generateHistoryData = () => {
      const data = [];
      const workTypes = ['office', 'wfh', 'hybrid'];
      const reasons = [
        'Regular attendance',
        'Traffic delay',
        'Medical appointment',
        'Personal work',
        'Family emergency',
        'System maintenance',
        'Client meeting',
        'Training session'
      ];

      for (let i = 0; i < 90; i++) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        
        const dayOfWeek = date.getDay();
        let status, workType, checkIn, checkOut, hours, reason;

        // Skip weekends for most entries
        if (dayOfWeek === 0 || dayOfWeek === 6) {
          if (Math.random() > 0.9) { // Occasional weekend work
            status = 'present';
            workType = 'wfh';
            checkIn = '10:00 AM';
            checkOut = '02:00 PM';
            hours = 4;
            reason = 'Emergency support';
          } else {
            continue;
          }
        } else {
          const rand = Math.random();
          if (rand < 0.02) {
            status = 'holiday';
            workType = null;
            reason = 'National Holiday';
          } else if (rand < 0.05) {
            status = 'absent';
            workType = null;
            reason = reasons[Math.floor(Math.random() * reasons.length)];
          } else if (rand < 0.1) {
            status = 'leave';
            workType = null;
            reason = 'Annual Leave';
          } else if (rand < 0.2) {
            status = 'late';
            workType = workTypes[Math.floor(Math.random() * workTypes.length)];
            checkIn = '09:' + (30 + Math.floor(Math.random() * 60)).toString().padStart(2, '0') + ' AM';
            checkOut = '06:' + Math.floor(Math.random() * 60).toString().padStart(2, '0') + ' PM';
            hours = 8 + Math.random() * 1;
            reason = 'Traffic delay';
          } else if (rand < 0.25) {
            status = 'half_day';
            workType = workTypes[Math.floor(Math.random() * workTypes.length)];
            checkIn = '09:' + Math.floor(Math.random() * 30).toString().padStart(2, '0') + ' AM';
            checkOut = '01:30 PM';
            hours = 4.5;
            reason = 'Medical appointment';
          } else {
            status = 'present';
            workType = workTypes[Math.floor(Math.random() * workTypes.length)];
            checkIn = '09:' + Math.floor(Math.random() * 20).toString().padStart(2, '0') + ' AM';
            const outHour = 17 + Math.floor(Math.random() * 2);
            const outMin = Math.floor(Math.random() * 60);
            checkOut = (outHour > 12 ? outHour - 12 : outHour) + ':' + outMin.toString().padStart(2, '0') + ' PM';
            hours = 8 + Math.random() * 2;
            reason = 'Regular attendance';
          }
        }

        data.push({
          id: i + 1,
          date: date.toISOString().split('T')[0],
          dayName: date.toLocaleDateString('en-US', { weekday: 'long' }),
          status,
          workType,
          checkIn,
          checkOut,
          hours: hours ? Math.round(hours * 10) / 10 : 0,
          reason,
          location: workType === 'office' ? 'Tower A, Floor 5' : 
                   workType === 'wfh' ? 'Home Office' : 
                   workType === 'hybrid' ? 'Tower B, Floor 3' : '--',
          approved: ['leave', 'half_day'].includes(status) ? Math.random() > 0.2 : true
        });
      }

      return data.reverse(); // Most recent first
    };

    setTimeout(() => {
      const data = generateHistoryData();
      setAttendanceHistory(data);
      setFilteredHistory(data);
      setLoading(false);
    }, 1000);
  }, []);

  const applyFilters = useCallback(() => {
    let filtered = [...attendanceHistory];

    // Date range filter
    const now = new Date();
    switch (filters.dateRange) {
      case 'last_week':
        const lastWeek = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        filtered = filtered.filter(item => new Date(item.date) >= lastWeek);
        break;
      case 'last_month':
        const lastMonth = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        filtered = filtered.filter(item => new Date(item.date) >= lastMonth);
        break;
      case 'last_3_months':
        const last3Months = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
        filtered = filtered.filter(item => new Date(item.date) >= last3Months);
        break;
      default:
        break;
    }

    // Status filter
    if (filters.status !== 'all') {
      filtered = filtered.filter(item => item.status === filters.status);
    }

    // Work type filter
    if (filters.workType !== 'all') {
      filtered = filtered.filter(item => item.workType === filters.workType);
    }

    // Search filter
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      filtered = filtered.filter(item => 
        item.reason?.toLowerCase().includes(searchTerm) ||
        item.location?.toLowerCase().includes(searchTerm) ||
        item.date.includes(searchTerm)
      );
    }

    setFilteredHistory(filtered);
    setCurrentPage(1);
  }, [filters, attendanceHistory]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'present':
        return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
      case 'absent':
        return <XCircleIcon className="w-5 h-5 text-red-500" />;
      case 'late':
        return <ExclamationTriangleIcon className="w-5 h-5 text-yellow-500" />;
      case 'half_day':
        return <ClockIcon className="w-5 h-5 text-orange-500" />;
      case 'leave':
        return <CalendarIcon className="w-5 h-5 text-blue-500" />;
      case 'holiday':
        return <CalendarIcon className="w-5 h-5 text-purple-500" />;
      default:
        return <ClockIcon className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'present':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'absent':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'late':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'half_day':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'leave':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'holiday':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      default:
        return 'bg-gray-100 text-gray-600 border-gray-200';
    }
  };

  // Pagination
  const totalPages = Math.ceil(filteredHistory.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = filteredHistory.slice(startIndex, endIndex);

  if (loading) {
    return (
      <div className="min-h-screen bg-apollo-gray">
        <Header />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-apollo-primary mx-auto mb-4"></div>
            <p className="text-gray-600">Loading attendance history...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-apollo-gray">
      <Header />
      
      {/* Page Header */}
      <div className="bg-gradient-to-r from-apollo-accent to-blue-600 text-white py-6 sm:py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex items-center justify-center mb-3 sm:mb-4">
              <DocumentTextIcon className="w-10 h-10 sm:w-12 sm:h-12 lg:w-16 lg:h-16 opacity-80" />
            </div>
            <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold mb-2 sm:mb-4">Attendance History</h1>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl opacity-90 max-w-2xl mx-auto px-4">
              Complete record of your attendance, leave applications, and work patterns
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Navigation */}
        <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row flex-wrap gap-2 sm:gap-3 lg:gap-4 justify-center lg:justify-start">
          <button
            onClick={() => navigate('/attendance')}
            className="bg-white border border-gray-300 text-gray-700 px-3 sm:px-4 lg:px-6 py-2 sm:py-3 rounded-lg sm:rounded-xl hover:bg-gray-50 transition-colors font-medium text-xs sm:text-sm lg:text-base"
          >
            ← Back to Dashboard
          </button>
          <button
            onClick={() => navigate('/attendance/monthly')}
            className="bg-white border border-gray-300 text-gray-700 px-3 sm:px-4 lg:px-6 py-2 sm:py-3 rounded-lg sm:rounded-xl hover:bg-gray-50 transition-colors font-medium text-xs sm:text-sm lg:text-base"
          >
            Monthly View
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-6 mb-6 sm:mb-8">
          <div className="flex flex-col lg:flex-row gap-3 sm:gap-4 items-center justify-between">
            {/* Search */}
            <div className="relative w-full lg:flex-1 lg:max-w-lg">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by reason, location, or date..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="w-full pl-8 sm:pl-10 pr-4 py-2 sm:py-3 border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-apollo-accent focus:border-transparent outline-none transition-all duration-200 text-sm sm:text-base"
              />
            </div>

            <div className="flex w-full lg:w-auto gap-2 sm:gap-3">
              {/* Filter Toggle */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex-1 lg:flex-none flex items-center justify-center space-x-2 bg-apollo-accent text-white px-3 sm:px-4 py-2 sm:py-3 rounded-lg sm:rounded-xl hover:bg-apollo-accent/90 transition-colors font-medium text-xs sm:text-sm"
              >
                <FunnelIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                <span>Filters</span>
              </button>

              {/* Export */}
              <button className="flex-1 lg:flex-none flex items-center justify-center space-x-2 bg-green-600 text-white px-3 sm:px-4 py-2 sm:py-3 rounded-lg sm:rounded-xl hover:bg-green-700 transition-colors font-medium text-xs sm:text-sm">
                <ArrowDownTrayIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                <span>Export</span>
              </button>
            </div>
          </div>

          {/* Advanced Filters */}
          {showFilters && (
            <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-gray-200 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Date Range</label>
                <select
                  value={filters.dateRange}
                  onChange={(e) => setFilters({ ...filters, dateRange: e.target.value })}
                  className="w-full p-2 sm:p-3 border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-apollo-accent focus:border-transparent outline-none text-xs sm:text-sm"
                >
                  <option value="all">All Time</option>
                  <option value="last_week">Last Week</option>
                  <option value="last_month">Last Month</option>
                  <option value="last_3_months">Last 3 Months</option>
                </select>
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  className="w-full p-2 sm:p-3 border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-apollo-accent focus:border-transparent outline-none text-xs sm:text-sm"
                >
                  <option value="all">All Status</option>
                  <option value="present">Present</option>
                  <option value="absent">Absent</option>
                  <option value="late">Late</option>
                  <option value="half_day">Half Day</option>
                  <option value="leave">Leave</option>
                  <option value="holiday">Holiday</option>
                </select>
              </div>

              <div className="sm:col-span-2 lg:col-span-1">
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Work Type</label>
                <select
                  value={filters.workType}
                  onChange={(e) => setFilters({ ...filters, workType: e.target.value })}
                  className="w-full p-2 sm:p-3 border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-apollo-accent focus:border-transparent outline-none text-xs sm:text-sm"
                >
                  <option value="all">All Types</option>
                  <option value="office">Office</option>
                  <option value="wfh">Work from Home</option>
                  <option value="hybrid">Hybrid</option>
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Results Summary */}
        <div className="mb-4 sm:mb-6">
          <p className="text-sm sm:text-base text-gray-600">
            Showing {currentItems.length} of {filteredHistory.length} records
            {filters.search && (
              <span> matching "{filters.search}"</span>
            )}
          </p>
        </div>

        {/* History Table */}
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg overflow-hidden">
          {/* Mobile Card View */}
          <div className="block md:hidden">
            <div className="divide-y divide-gray-200">
              {currentItems.map((record) => (
                <div key={record.id} className="p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {new Date(record.date).toLocaleDateString()}
                      </div>
                      <div className="text-xs text-gray-500">{record.dayName}</div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {getStatusIcon(record.status)}
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(record.status)}`}>
                        {record.status.replace('_', ' ')}
                      </span>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-500">Work Type:</span>
                      <div className="font-medium">
                        {record.workType ? (
                          <span className="capitalize">
                            {record.workType === 'wfh' ? 'Work from Home' : record.workType}
                          </span>
                        ) : (
                          <span className="text-gray-400">--</span>
                        )}
                      </div>
                    </div>
                    
                    <div>
                      <span className="text-gray-500">Hours:</span>
                      <div className="font-medium">
                        {record.hours > 0 ? `${record.hours}h` : '--'}
                      </div>
                    </div>
                    
                    <div>
                      <span className="text-gray-500">Check In:</span>
                      <div className="font-medium">{record.checkIn || '--'}</div>
                    </div>
                    
                    <div>
                      <span className="text-gray-500">Check Out:</span>
                      <div className="font-medium">{record.checkOut || '--'}</div>
                    </div>
                  </div>
                  
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <div className="text-xs text-gray-500 mb-1">Location:</div>
                    <div className="text-sm text-gray-900 mb-2">{record.location}</div>
                    <div className="text-xs text-gray-500 mb-1">Notes:</div>
                    <div className="text-sm text-gray-600">{record.reason}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Work Type
                  </th>
                  <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Time
                  </th>
                  <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Hours
                  </th>
                  <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Location
                  </th>
                  <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Notes
                  </th>
                  <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentItems.map((record) => (
                  <tr key={record.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {new Date(record.date).toLocaleDateString()}
                        </div>
                        <div className="text-sm text-gray-500">{record.dayName}</div>
                      </div>
                    </td>
                    <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        {getStatusIcon(record.status)}
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(record.status)}`}>
                          {record.status.replace('_', ' ')}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                      {record.workType ? (
                        <span className="text-sm text-gray-900 capitalize">
                          {record.workType === 'wfh' ? 'Work from Home' : record.workType}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400">--</span>
                      )}
                    </td>
                    <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                      {record.checkIn && record.checkOut ? (
                        <div className="text-sm">
                          <div className="text-gray-900">{record.checkIn}</div>
                          <div className="text-gray-500">{record.checkOut}</div>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">--</span>
                      )}
                    </td>
                    <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                      {record.hours > 0 ? (
                        <span className="text-sm font-medium text-gray-900">{record.hours}h</span>
                      ) : (
                        <span className="text-sm text-gray-400">--</span>
                      )}
                    </td>
                    <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">{record.location}</span>
                    </td>
                    <td className="px-4 lg:px-6 py-4">
                      <span className="text-sm text-gray-600">{record.reason}</span>
                    </td>
                    <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                      <button className="text-apollo-accent hover:text-apollo-primary transition-colors">
                        <EyeIcon className="w-5 h-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
              <div className="flex items-center justify-between">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
                      <span className="font-medium">{Math.min(endIndex, filteredHistory.length)}</span> of{' '}
                      <span className="font-medium">{filteredHistory.length}</span> results
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                      <button
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronLeftIcon className="h-5 w-5" />
                      </button>
                      {[...Array(totalPages)].map((_, index) => {
                        const page = index + 1;
                        return (
                          <button
                            key={page}
                            onClick={() => setCurrentPage(page)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                              currentPage === page
                                ? 'z-10 bg-apollo-accent border-apollo-accent text-white'
                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                            }`}
                          >
                            {page}
                          </button>
                        );
                      })}
                      <button
                        onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronRightIcon className="h-5 w-5" />
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AttendanceHistory;
